import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { InView } from 'react-intersection-observer';

import { TemplateClassNames } from "../../../../../types/template-layouts";
import { IQuestion, PageModule } from "../../../../../types/working-model";
import './questions.scss';
import { ParamsProps } from "../../../live-event";
import AskQuestion from "./ask-question";
import QuestionCard from "./question-card";
import { FetchModuleQuestions } from "../../../../../connection/questions";
import WaitingIndicator from "../../../../general-ui/waiting-indicator/waiting-indicator";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { getStorageItem } from "../../../../../utils/local-storage";
import { setModuleQuestions } from "../../../../../store/actions/event/questions-actions";
import { useDispatch } from "react-redux";
import useQuestionsSockets from "./use-questions-sockets";
import { useTranslation } from "react-i18next";
import { getStylingOverrides } from "../../../utils";
import classNames from "classnames";

interface IQuestionsProps {
	module: PageModule;
	template: TemplateClassNames;
	session: number;
	session_uuid: string;
}

export const QUESTIONS_PAGE_LIMIT = 50;

const SessionQuestions: React.FC<IQuestionsProps> = ({ module, template, session, session_uuid }) => {

	const { t } = useTranslation("session");

	const dispatch = useDispatch();

	const [fetching, setFetching] = useState(false);
	const [initialized, setInitialized] = useState(false);
	const [hasFocus, setHasFocus] = useState(false);

	const event = useTypedSelector(state => state.LiveEventReducer.eventBundle?.event);
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const channel = useTypedSelector(state => state.LiveEventReducer.eventBundle?.channel);
	const blProfileUser = useTypedSelector(state => state.LiveEventReducer.blProfileUser);
	const questions = useTypedSelector(state => state.QuestionsReducer.questions);
	const comments = useTypedSelector(state => state.QuestionsReducer.comments);

	const { language }: ParamsProps = useParams();
	const eventUuid = eventBundle?.uuid;

	useQuestionsSockets({ questions, comments, language, session_uuid, blProfileUser, module });
	const { backgroundColor, backgroundImg } = getStylingOverrides(module.styling_overrides);

	const module_id = module.id;
	useEffect(() => {
		// fetch the newest batch of QUESTIONS_PAGE_LIMIT comments for this question
		const fetchInitialBatch = async () => {
			try {
				if (!module_id) return;
				setFetching(true);
				const token = getStorageItem('Bl-Profile-Token') ?? '';
				const result: { questions: IQuestion[], has_more: boolean } = await FetchModuleQuestions(module_id, language, token);
				const questionsMapped = new Map();
				result?.questions?.forEach((qn: any) => {
					questionsMapped.set(qn.id, qn);
				});
				dispatch(setModuleQuestions({ [module_id]: questionsMapped }));
				setFetching(false);
			} catch (e) {
				console.error(e);
				setFetching(false);
			}
		};

		if (!initialized && module_id) {
			setInitialized(true);
			fetchInitialBatch();
		}

	}, [initialized, module_id, language, dispatch]);


	let title = t("Questions");
	if (module?.content?.custom_heading?.[language] ?? module?.content?.custom_heading?.base) {
		title = module?.content?.custom_heading?.[language] || module?.content?.custom_heading?.base;
	}

	const onFocus = useCallback(() => setHasFocus(true), []);
	const onBlur = useCallback(() => setHasFocus(false), []);

	if (!channel || !event || !session_uuid || !eventUuid || !module_id || !questions[module_id]) return null;
	return (
		<div
			className={classNames(
				"questions-container session-module",
				backgroundColor,
				template,
				{ focused: hasFocus }
			)}
			style={backgroundImg}
		>
			<h6 className="questions-title-mobile">{title}</h6>
			<h5 className="questions-title">{title}</h5>
			<div className="main-question">
				<AskQuestion
					module={module}
					sessionUuid={session_uuid}
					onFocus={onFocus}
					onBlur={onBlur}
				/>
			</div>

			{fetching ? <WaitingIndicator /> : null}
			{Array.from(questions[module_id]).map((question) => (
				<InView key={question[0]}>
					{({ inView, ref }) => {
						return (
							<QuestionCard
								ref={ref}
								inView={inView}
								question={question[1]}
								module={module}
								session={session}
								session_uuid={session_uuid}
							/>
						);
					}}
				</InView>
			))}
		</div>
	);
};

export default SessionQuestions;